// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { MarketingConsent } from '@iheartjane/menu';
import type { ReeferTheme } from '@iheartjane/reefer';
import { CheckboxField, Flex, Link, Typography } from '@iheartjane/reefer';
import type { Config } from 'final-form';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { Form } from 'react-final-form';

import { useEcommApp } from '@jane/shared/providers';
import { monolithPaths as paths } from '@jane/shared/util';

import { TextInputField } from '../../component-library/text-input/text-input';
import { selectLogin } from '../../customer/redux/login';
import { selectStore } from '../../customer/redux/store';
import { useCustomerSelector } from '../../customer/selectors';
import { useDisplayGTITerms } from '../../hooks/useDisplayGTITerms';
import type { ShowLoginProps } from '../login/common';
import {
  CustomFieldError,
  PrimaryLoginButton,
  SUBMIT_ERROR_TEXT,
  SubmitErrorMessage,
} from '../login/common';
import { PhoneInputField } from '../phoneInputField';

export interface RegistrationFormProps {
  embeddedMode?: boolean;
  onSubmit: Config<RegistrationFormValues>['onSubmit'];
  setShowLogin: (arg: boolean) => void;
  theme?: ReeferTheme;
}

const FinePrintBase = styled.div<{ gtiColors: boolean }>(
  ({ theme, gtiColors }) => ({
    color: gtiColors ? theme.colors.grays.black : theme.colors.text.light,
    display: 'inline-block',
  })
);

const FinePrint = ({ children }: PropsWithChildren) => {
  const displayGTITerms = useDisplayGTITerms();

  return <FinePrintBase gtiColors={displayGTITerms}>{children}</FinePrintBase>;
};

export const RegistrationFinePrint = () => {
  const { store } = useCustomerSelector(selectStore);

  const displayGTITerms = useDisplayGTITerms();
  const storeInOhio = store['state'] === 'Ohio';

  const gtiTermsAndConditions = storeInOhio
    ? 'https://oh.risecannabis.com/terms/'
    : 'https://risecannabis.com/terms/';

  const gtiPrivacyPolicy = storeInOhio
    ? 'https://oh.risecannabis.com/privacy-policy/'
    : 'https://risecannabis.com/privacy-policy/';

  const gtiNoticeOfPrivacyPractices = storeInOhio
    ? 'https://oh.risecannabis.com/notice-of-privacy-practices/'
    : 'https://risecannabis.com/notice-of-privacy-practices/';

  return (
    <Typography
      as="div"
      color="grays-mid"
      mb={20}
      mt={16}
      textAlign="left"
      variant="mini"
    >
      {' '}
      <FinePrint>By continuing, I agree to Jane&apos;s</FinePrint>{' '}
      <Link color="grays-black" href={paths.termsOfUse()}>
        Terms of Service
      </Link>
      <FinePrint>&nbsp;&amp;&nbsp;</FinePrint>
      <Link color="grays-black" href={paths.privacyPolicy()}>
        Privacy Policy
      </Link>
      {displayGTITerms && (
        <>
          <FinePrint>&nbsp;&amp; Green Thumb&apos;s&nbsp;</FinePrint>
          <Link color="grays-black" href={gtiTermsAndConditions}>
            Terms and Conditions
          </Link>
          <FinePrint>,&nbsp;</FinePrint>
          <Link color="grays-black" href={gtiPrivacyPolicy}>
            Privacy Policy
          </Link>
          <FinePrint>&nbsp;&amp;&nbsp;</FinePrint>
          <Link color="grays-black" href={gtiNoticeOfPrivacyPractices}>
            Notice of Privacy Practices
          </Link>
        </>
      )}
    </Typography>
  );
};

type Props = {
  onSubmit: Config<RegistrationFormValues>['onSubmit'];
  theme?: ReeferTheme;
} & ShowLoginProps;

interface Errors {
  confirmPassword?: string;
  email?: string;
  nickname?: string;
  password?: string;
  phone?: string;
}

const validate: Config<RegistrationFormValues>['validate'] = (values) => {
  const errors: Errors = {};
  if (!values.email) {
    errors.email = 'Required';
  }
  if (!values.phone) {
    errors.phone = 'Required';
  }
  if (values.password && values.password.length < 10) {
    errors.password = 'Must be at least 10 characters';
  }
  return errors;
};

interface RegistrationFormValues {
  email: string;
  nickname?: string;
  password: string;
  phone: string;
  promotionsOptIn: boolean;
}

export const RegistrationForm = ({ onSubmit, setShowLogin }: Props) => {
  const loginState = useCustomerSelector(selectLogin);
  const { appMode } = useEcommApp();
  const isMarketplace = appMode === 'default';

  // Store initial values in state so if loginState changes during submission
  // error, the form isn't reset and lose displaying server errors.
  const [initialValues] = useState<RegistrationFormValues>({
    nickname: '',
    email: loginState?.user?.email || '',
    phone: loginState?.user?.phone || '',
    password: '',
    promotionsOptIn: isMarketplace ? true : undefined,
  });

  const renderEmailError = () => {
    return (
      <CustomFieldError>
        This email is already taken.{' '}
        <button
          // eslint-disable-next-line react/no-unknown-property, react/forbid-dom-props -- legacy emotion
          css={{ color: 'var(--colors-brand-purple-main)' }}
          onClick={() => setShowLogin && setShowLogin(true)}
        >
          Sign in
        </button>
        ?
      </CustomFieldError>
    );
  };

  return (
    <Form<RegistrationFormValues>
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
      render={({
        handleSubmit,
        submitting,
        hasValidationErrors,
        hasSubmitErrors,
        submitFailed,
      }) => (
        <form onSubmit={handleSubmit}>
          <TextInputField
            variant="material"
            name="email"
            type="email"
            label="Email address"
            id="email"
            autoComplete="email"
            renderSubmitError={renderEmailError}
          />
          <PhoneInputField
            name="phone"
            label="Phone number"
            id="phone"
            helperText="Used only for reservation notices"
            autoComplete="tel"
          />
          <TextInputField
            name="password"
            type="password"
            label="Password"
            id="password"
            autoComplete="new-password"
          />
          {isMarketplace && (
            <Flex mb={24} flexDirection="row">
              <CheckboxField
                disableMobileInputStyling
                name="promotionsOptIn"
                label="Promotional emails"
                labelHidden
              />
              <MarketingConsent />
            </Flex>
          )}
          <Flex flexDirection="column" mt={32} part="sing-in">
            <PrimaryLoginButton
              onClick={handleSubmit}
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              label="Sign up"
              loading={submitting}
            />
          </Flex>
          {(hasSubmitErrors || (submitFailed && hasValidationErrors)) && (
            <SubmitErrorMessage>{SUBMIT_ERROR_TEXT}</SubmitErrorMessage>
          )}
        </form>
      )}
    />
  );
};
